import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Chart } from 'chart.js';
import {
  Observable,
  Subject,
  Subscription,
  filter,
  interval,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';

import { UserStateService } from 'src/app/core/services/authentication/user-state.service';
import { AppNotificationService } from './core/services/app-notification.service';
import { AppStateService } from './core/services/app-state.service';
import { AppStyleService } from './core/services/app-style.service';
import { AuthTokenService } from './core/services/authentication/auth-token.service';
import { DialogService } from './core/services/dialog.service';
import { SocketService } from './core/services/socket.service';
import { AppVersionService } from './core/services/version.service';
import { isPublicRoute } from './core/utils/util';
import { TranslationService } from './i18n';
import { locale as enLang } from './i18n/en';
import { locale as faLang } from './i18n/fa';
import { AppIntroComponent } from './layout/components/app-intro/app-intro.component';
import { AppIntroService } from './layout/components/app-intro/app-intro.service';
import { NotificationsComponent } from './layout/components/explore-main-drawer/notifications/notifications.component';
import { StartupErrorComponent } from './layout/components/startup-error/startup-error.component';
import { StartupLoadingComponent } from './layout/components/startup-loading/startup-loading.component';

// eslint-disable-next-line
function promptUser(event: VersionReadyEvent): boolean {
  return true;
}

@Component({
  selector: 'body[root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    StartupErrorComponent,
    StartupLoadingComponent,
    AppIntroComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private translationService = inject(TranslationService);
  private userSession = inject(UserStateService);
  private router = inject(Router);
  private appNotificationService = inject(AppNotificationService);
  private dialogService = inject(DialogService);

  readonly notificationsComponent = viewChild(NotificationsComponent);

  appIntroService = inject(AppIntroService);
  appStyleService = inject(AppStyleService);
  websocketService = inject(SocketService);
  updates = inject(SwUpdate);
  appVersionService = inject(AppVersionService);

  isVisibleAppIntro$: Observable<boolean>;
  appChartFont = 'Vazirmatn';
  isLtr = false;
  header: string;
  intervalSubscription: Subscription;
  timerIntervalId: any;
  isShowNotification = false;
  protected onDestroy$ = new Subject<void>();

  constructor() {
    this.checkForAppUpdates();

    this.translationService.loadTranslations(faLang, enLang);
    effect(() => {
      this.userSession.userState();
      this.checkForceChangePassword();
      this.appIntroService.visible();
      this.handleAppIntroDisplayStatus();
    });
  }

  ngOnInit() {
    this.detectChangeStorage();
    this.configAppChartFont();
    this.appVersionService.getVersion();

    if (AuthTokenService.getAccessToken()) {
      this.getNotifications();

      this.isShowNotification = true;
      this.websocketService.connect();
    }

    this.appStyleService.initThemeMode();
  }

  changeIsShowNotification() {
    if (this.intervalSubscription) this.intervalSubscription.unsubscribe();
    this.isShowNotification = true;
    this.autoUpdateNotifications();
  }

  autoUpdateNotifications() {
    this.startTimer();
    this.intervalSubscription = interval(26000) // Set the interval to 25000 milliseconds (25 seconds)
      .pipe(takeUntil(this.onDestroy$))
      .pipe(
        switchMap(() => {
          if (this.isShowNotification)
            return this.appNotificationService.getNotificationsApi();
          else return of({});
        }),
      )
      .subscribe(() => {
        this.startTimer();
      });
  }
  startTimer() {
    if (this.timerIntervalId) clearInterval(this.timerIntervalId);
    this.appNotificationService.notificationTimerValue.set(0);
    this.timerIntervalId = setInterval(() => {
      let notificationTimerValue =
        this.appNotificationService.notificationTimerValue();
      this.appNotificationService.notificationTimerValue.set(0);
      if (notificationTimerValue <= 25)
        this.appNotificationService.notificationTimerValue.set(
          (notificationTimerValue += 1),
        );
    }, 1000);
  }

  onUpdateNotifications() {
    const notificationsComponent = this.notificationsComponent();
    if (notificationsComponent) notificationsComponent.getNotificationListApi();
  }

  ngAfterViewInit(): void {
    this.header = AppStateService.header;
    if (this.header) {
      document.head.insertAdjacentHTML('beforeend', this.header);
    }
  }

  getNotifications() {
    this.appNotificationService.getNotificationsApi().subscribe();
  }

  checkForceChangePassword() {
    if (this.userSession.userState()?.changePasswordRequired)
      this.router.navigate(['/account/change-password']);
  }

  detectChangeStorage() {
    addEventListener('storage', (event: StorageEvent) => {
      if (event.key == 'act' || event.key == 'rft') {
        if (!isPublicRoute()) {
          window.location.replace(window.location.origin);
        }
      }
    });
  }

  handleAppIntroDisplayStatus() {
    if (this.appIntroService.visible())
      document.getElementsByTagName('html')[0].style.overflow = '!hidden';
    else document.getElementsByTagName('html')[0].style.overflow = 'scroll';
  }

  configAppChartFont() {
    this.isLtr =
      document.getElementsByTagName('html')[0].getAttribute('dir') == 'ltr';
    if (this.isLtr) this.appChartFont = 'Poppins';
    else this.appChartFont = 'Vazirmatn';
    Chart.defaults.font.family = this.appChartFont;
    Chart.defaults.font.size = 14;
  }

  checkForAppUpdates(): void {
    if (!this.updates.isEnabled) return;

    this.updates.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe((evt) => {
        if (promptUser(evt) && isPublicRoute())
          this.dialogService.loadComponentDialog();
      });
  }

  ngOnDestroy() {
    this.unSubs();
    this.onDestroy$.complete();
    this.websocketService.close();
  }

  unSubs() {
    this.onDestroy$.next();
  }
}
